import type { Cue } from '../model/cue.ts';
import { createImageCue } from '../model/cue.ts';
import type { ImageParsedTrack, TextParsedTrack } from '../model/track.ts';
import { stripDirectionalityMarks } from './clock.ts';
import { parseJson3Asr, parseJson3Normal } from './json3.ts';
import { parseImageTtml } from './imageTtml.ts';
import { parseNetflixTextTtml, parsePrimeTextTtml } from './ttml.ts';
import { parseWebVtt } from './webvtt.ts';

export type ParserKind =
  | 'json3-normal'
  | 'json3-asr'
  | 'webvtt'
  | 'ttml-netflix'
  | 'ttml-prime'
  | 'image-ttml';

export type ParseOptions = {
  asrLastItemDurationMs?: number;
  imageMap?: Record<string, string>;
};

export function textParsedTrackToCues(parsed: TextParsedTrack): Cue[] {
  return parsed.tracks.map((track) => {
    // ref: begin * 1000 with undefined/null → NaN (never matches activeCuesAt); do not coerce to 0
    const start = (track.begin == null ? NaN : track.begin) * 1000;
    const end = (track.end == null ? NaN : track.end) * 1000;
    const text = stripDirectionalityMarks(track.html);
    return {
      start,
      end,
      text,
      segs: [{ start, end, text }],
    };
  });
}

export function imageParsedTrackToCues(parsed: ImageParsedTrack): Cue[] {
  return parsed.tracks.map((track) =>
    createImageCue(
      // AUDIT4 F-011: same NaN semantics as textParsedTrackToCues / legacyParsedTrackToCues
      (track.begin == null ? NaN : track.begin) * 1000,
      (track.end == null ? NaN : track.end) * 1000,
      track.src ?? '',
      track.extent,
      track.origin,
    ),
  );
}

/** Unified entry: raw fixture input → {@link Cue}[]. */
export function parseCues(kind: ParserKind, raw: string | object, options: ParseOptions = {}): Cue[] {
  switch (kind) {
    case 'json3-normal':
      return parseJson3Normal(raw as Parameters<typeof parseJson3Normal>[0]);
    case 'json3-asr':
      return parseJson3Asr(raw as Parameters<typeof parseJson3Asr>[0], options.asrLastItemDurationMs);
    case 'webvtt':
      return textParsedTrackToCues(parseWebVtt(String(raw)));
    case 'ttml-netflix':
      return textParsedTrackToCues(parseNetflixTextTtml(String(raw)));
    case 'ttml-prime':
      return textParsedTrackToCues(parsePrimeTextTtml(String(raw)));
    case 'image-ttml':
      return imageParsedTrackToCues(parseImageTtml(String(raw), options.imageMap ?? {}));
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export { parseJson3Asr, parseJson3Normal } from './json3.ts';
export { parseWebVtt } from './webvtt.ts';
export { parseNetflixTextTtml, parsePrimeTextTtml } from './ttml.ts';
export { parseImageTtml } from './imageTtml.ts';
export { parseClockTime, parseTickTime, stripDirectionalityMarks } from './clock.ts';
